/**
 * @component ry-rating
 * @category  form
 * @summary   Star/heart rating selector (Fomantic UI `.ui.rating` equivalent). Renders `[max]` icons; the first `[value]` are filled. Click an icon to set value; click the current value again to clear it (when `[clearable]`). Keyboard arrows adjust; Home/End jump to extremes. Emits `ry:change` on every change with the new value.
 *
 * Reference: Fomantic UI's rating module. ry-rating uses inline SVG/text
 * glyphs (★/♥/⚪) for icons — no font dependency. For custom icons compose
 * with the consumer's icon system by overriding the rendered glyph via CSS.
 *
 * @attribute value      number?   default=0     Current rating (0 to max). Clamped to [0, max] at read time.
 * @attribute max        number?   default=5     Total number of icons rendered.
 * @attribute icon       'star' | 'heart'?   default="star"   Glyph shape. Pure CSS hook — JS doesn't change behavior.
 * @attribute readonly   boolean   default=false   When present, click/keyboard interaction is suppressed. Display-only.
 * @attribute clearable  boolean   default=false   When present, clicking the CURRENTLY-highest filled icon clears the rating to 0.
 * @attribute size       'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge'?   default="medium"   Glyph scale. Fomantic UI parity (mini 0.7em → huge 2em). Pure CSS — no JS behavior change.
 *
 * @property  value      number    Live numeric value clamped to [0, max].
 * @property  max        number    Live numeric max.
 *
 * @method    set(value)  Set the rating programmatically. Fires `ry:change`.
 *
 * @event ry:beforechange
 *        detail: { value: number, max: number, previous: number }
 *        fires:  at the top of `set()` — icon click, keyboard (arrows / Home / End), clearable-clear, and programmatic `set(v)` — before the `value` attribute changes, and only when the (clamped) value would actually change. `value` is the NEXT rating, `previous` the current one. The raw `value` property setter / `setAttribute('value')` path is attribute-driven and is not intercepted.
 *        cancelable: preventDefault() leaves the rating unchanged; `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: number, max: number }
 *        fires:  every time the value changes (user click, keyboard, or programmatic `set()`). Setting the SAME value via setAttribute does NOT fire (Custom Element semantic).
 *
 * @keyboard  ArrowRight / ArrowUp — increment value by 1 (clamped to max). ArrowLeft / ArrowDown — decrement by 1 (clamped to 0). Home — set to 0 (cleared). End — set to max. All keys are no-ops when [readonly].
 *
 * @state     `data-ry-state`: `"readonly"` when `[readonly]` is present; otherwise unset.
 *
 * @content   None — `<ry-rating>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-rating[max][value][readonly?][clearable?]   role="slider"   aria-valuemin="0" aria-valuemax=max aria-valuenow=value   tabindex="0"
 *     > button.ry-rating__icon   [data-ry-target="icon"]   [data-index="0"]   [data-active="true|false"]   aria-label="N out of max"
 *     > button.ry-rating__icon   [data-ry-target="icon"]   [data-index="1"]   ...
 *     > ...repeated max times
 *
 * @gotcha  The host gets `role="slider"` (not `role="radiogroup"`). This is intentional: rating IS a numeric slider with discrete steps. Screen readers announce the current value via `aria-valuenow`.
 *
 * @gotcha  `[clearable]` only clears on clicking the EXACT current value. Clicking any lower-numbered icon sets the rating to that number (does not clear). Clicking higher-numbered icons always sets to the new number.
 *
 * @gotcha  Keyboard interaction operates on the HOST, not individual icons. The host has `tabindex="0"`; the inner icon buttons are `tabindex="-1"` (the inner role="button" semantics are sacrificed for cleaner Tab navigation — the host owns the focus).
 *
 * @gotcha  `[readonly]` removes interactivity but keeps the slider role + aria-valuenow. Screen readers still announce the value; the consumer is signaling "this is a displayed rating, not an input."
 *
 * @gotcha  Live updates: changing `value` / `max` / `readonly` / `clearable` are all observed and re-render. Changing `icon` is NOT observed — set it before mount or replace the element.
 *
 * @example basic
 * ```html
 * <ry-rating value="3"></ry-rating>
 * ```
 *
 * @example heart-icon
 * ```html
 * <ry-rating value="4" icon="heart"></ry-rating>
 * ```
 *
 * @example custom-max
 * ```html
 * <ry-rating value="7" max="10"></ry-rating>
 * ```
 *
 * @example readonly
 * ```html
 * <ry-rating value="4" readonly></ry-rating>
 * ```
 *
 * @example clearable
 * ```html
 * <ry-rating value="3" clearable></ry-rating>
 * ```
 *
 * @example sizes
 * ```html
 * <div style="display: flex; flex-direction: column; gap: 0.75rem; align-items: flex-start;">
 *   <ry-rating value="3" size="mini"></ry-rating>
 *   <ry-rating value="3" size="tiny"></ry-rating>
 *   <ry-rating value="3" size="small"></ry-rating>
 *   <ry-rating value="3" size="medium"></ry-rating>
 *   <ry-rating value="3" size="large"></ry-rating>
 *   <ry-rating value="3" size="big"></ry-rating>
 *   <ry-rating value="3" size="huge"></ry-rating>
 * </div>
 * ```
 *
 * @tutorial mount-renders-max-icons
 * ```js
 * const el = mount('<ry-rating value="3" max="5"></ry-rating>');
 * const icons = el.querySelectorAll('[data-ry-target="icon"]');
 * await expectEqual(icons.length, 5, 'max=5 icons rendered');
 * ```
 *
 * @tutorial active-icons-reflect-value
 * ```js
 * const el = mount('<ry-rating value="3" max="5"></ry-rating>');
 * const icons = el.querySelectorAll('[data-ry-target="icon"]');
 * // First 3 should be active, last 2 inactive
 * await expectAttr(icons[0], 'data-active', 'true');
 * await expectAttr(icons[2], 'data-active', 'true');
 * await expectAttr(icons[3], 'data-active', 'false');
 * await expectAttr(icons[4], 'data-active', 'false');
 * ```
 *
 * @tutorial click-icon-sets-value
 * ```js
 * const el = mount('<ry-rating value="0" max="5"></ry-rating>');
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.value));
 * const icons = el.querySelectorAll('[data-ry-target="icon"]');
 * icons[2].click();  // click the 3rd icon -> value 3
 * await expectEqual(el.value, 3);
 * await expectEqual(fired[0], 3);
 * ```
 *
 * @tutorial clearable-click-current-clears
 * ```js
 * // Wrong assumption: clicking the active icon increments or no-ops.
 * // With [clearable], clicking the icon EQUAL to current value clears to 0.
 * const el = mount('<ry-rating value="3" max="5" clearable></ry-rating>');
 * const icons = el.querySelectorAll('[data-ry-target="icon"]');
 * icons[2].click();  // click the 3rd icon (current value) -> clear
 * await expectEqual(el.value, 0, 'clicking current value clears');
 * ```
 *
 * @tutorial clearable-click-lower-does-not-clear
 * ```js
 * // Clicking a lower icon than current sets to that lower value, not clear.
 * const el = mount('<ry-rating value="3" max="5" clearable></ry-rating>');
 * const icons = el.querySelectorAll('[data-ry-target="icon"]');
 * icons[0].click();
 * await expectEqual(el.value, 1, 'clicking lower icon sets to that value');
 * ```
 *
 * @tutorial readonly-suppresses-click
 * ```js
 * const el = mount('<ry-rating value="2" max="5" readonly></ry-rating>');
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * const icons = el.querySelectorAll('[data-ry-target="icon"]');
 * icons[4].click();
 * await expectEqual(el.value, 2, 'value unchanged');
 * await expectEqual(fired.length, 0, 'no ry:change fired');
 * ```
 *
 * @tutorial keyboard-arrows-adjust
 * ```js
 * const el = mount('<ry-rating value="2" max="5"></ry-rating>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.value, 3, 'ArrowRight increments');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowLeft', bubbles: true }));
 * await expectEqual(el.value, 2, 'ArrowLeft decrements');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'End', bubbles: true }));
 * await expectEqual(el.value, 5, 'End jumps to max');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Home', bubbles: true }));
 * await expectEqual(el.value, 0, 'Home jumps to 0');
 * ```
 *
 * @tutorial keyboard-clamps-at-edges
 * ```js
 * // ArrowRight past max stays at max; ArrowLeft past 0 stays at 0.
 * const el = mount('<ry-rating value="5" max="5"></ry-rating>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.value, 5, 'cannot exceed max');
 * el.setAttribute('value', '0');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowLeft', bubbles: true }));
 * await expectEqual(el.value, 0, 'cannot go below 0');
 * ```
 *
 * @tutorial readonly-suppresses-keyboard
 * ```js
 * const el = mount('<ry-rating value="2" max="5" readonly></ry-rating>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.value, 2, 'arrow keys ignored when readonly');
 * ```
 *
 * @tutorial aria-attributes-mirror-state
 * ```js
 * const el = mount('<ry-rating value="3" max="5"></ry-rating>');
 * await expectAttr(el, 'role', 'slider');
 * await expectAttr(el, 'aria-valuemin', '0');
 * await expectAttr(el, 'aria-valuemax', '5');
 * await expectAttr(el, 'aria-valuenow', '3');
 * el.set(4);
 * await expectAttr(el, 'aria-valuenow', '4', 'aria-valuenow tracks value');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange leaves the rating where it was —
 * // click and keyboard both route through set(), so one veto covers both.
 * const el = mount('<ry-rating value="2" max="5"></ry-rating>');
 * const changed = [];
 * el.addEventListener('ry:change', () => changed.push(1));
 * let seen = null;
 * el.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * const icons = el.querySelectorAll('[data-ry-target="icon"]');
 * icons[4].click();
 * await expectEqual(seen.value, 5, 'detail.value is the NEXT rating');
 * await expectEqual(seen.previous, 2, 'detail.previous is the current rating');
 * await expectEqual(el.value, 2, 'vetoed click leaves the rating at 2');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * el.addEventListener('ry:beforechange', (e) => e.preventDefault(), { once: true });
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.value, 2, 'vetoed ArrowRight leaves it at 2');
 * icons[4].click();
 * await expectEqual(el.value, 5, 'without the veto, the click sets 5');
 * await expectEqual(changed.length, 1, 'ry:change fired once');
 * ```
 *
 * @see ry-slider
 * @see ry-progress
 */

import { RyElement } from '../core/ry-element.js';

interface RatingChangeDetail {
  value: number;
  max: number;
}

interface RatingBeforeChangeDetail extends RatingChangeDetail {
  previous: number;
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

export class RyRating extends RyElement {
  static get observedAttributes(): string[] {
    return ['value', 'max', 'readonly', 'clearable'];
  }

  setup(): void {
    this.#render();
    this.#applyAria();
    this.on(this, 'keydown', this.#handleKeydown);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;

    if (name === 'max') {
      this.#render();
      this.#applyAria();
      return;
    }
    if (name === 'value') {
      this.#updateActive();
      this.#applyAria();
      this.emit<RatingChangeDetail>('change', { value: this.value, max: this.max });
      return;
    }
    if (name === 'readonly') {
      this.#applyAria();
      this.#updateReadonlyState();
      return;
    }
    if (name === 'clearable') {
      // No visible change; just an interaction-time read.
      return;
    }
  }

  get value(): number {
    const raw = parseFloat(this.getAttribute('value') ?? '0');
    return clamp(Number.isFinite(raw) ? raw : 0, 0, this.max);
  }

  set value(v: number) {
    this.setAttribute('value', String(v));
  }

  get max(): number {
    const raw = parseFloat(this.getAttribute('max') ?? '5');
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 5;
  }

  set max(v: number) {
    this.setAttribute('max', String(v));
  }

  set(value: number): void {
    const clamped = clamp(value, 0, this.max);
    const previous = this.value;
    // Interception point — click, keyboard, and programmatic set() all land
    // here. Only a real transition is vetoable; the attribute write below is
    // what fires ry:change (via attributeChangedCallback).
    if (clamped !== previous && !this.emit<RatingBeforeChangeDetail>('beforechange', {
      value: clamped,
      max: this.max,
      previous,
    })) return;
    this.setAttribute('value', String(clamped));
  }

  #render(): void {
    const max = this.max;
    const icons: string[] = [];
    for (let i = 0; i < max; i++) {
      icons.push(`<button
        data-ry-target="icon"
        data-index="${i}"
        data-active="false"
        class="ry-rating__icon"
        type="button"
        tabindex="-1"
        aria-label="${i + 1} out of ${max}"
      ></button>`);
    }
    this.innerHTML = icons.join('');
    this.#bindIconClicks();
    this.#updateActive();
    this.#updateReadonlyState();
  }

  #bindIconClicks(): void {
    this.$$<HTMLButtonElement>('[data-ry-target="icon"]').forEach((btn) => {
      this.on(btn, 'click', this.#handleIconClick);
    });
  }

  #handleIconClick = (event: MouseEvent): void => {
    if (this.hasAttribute('readonly')) return;
    const btn = event.currentTarget as HTMLElement;
    const index = parseInt(btn.getAttribute('data-index') ?? '0', 10);
    const newValue = index + 1;
    if (this.hasAttribute('clearable') && newValue === this.value) {
      this.set(0);
    } else {
      this.set(newValue);
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (this.hasAttribute('readonly')) return;
    const v = this.value;
    const m = this.max;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        this.set(Math.min(v + 1, m));
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        this.set(Math.max(v - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        this.set(0);
        break;
      case 'End':
        event.preventDefault();
        this.set(m);
        break;
    }
  };

  #updateActive(): void {
    const v = this.value;
    this.$$<HTMLElement>('[data-ry-target="icon"]').forEach((btn, i) => {
      btn.setAttribute('data-active', i < v ? 'true' : 'false');
    });
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'slider');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    this.setAttribute('aria-valuemin', '0');
    this.setAttribute('aria-valuemax', String(this.max));
    this.setAttribute('aria-valuenow', String(this.value));
    if (this.hasAttribute('readonly')) {
      this.setAttribute('aria-readonly', 'true');
    } else {
      this.removeAttribute('aria-readonly');
    }
  }

  #updateReadonlyState(): void {
    if (this.hasAttribute('readonly')) {
      this.dataset.ryState = 'readonly';
    } else if (this.dataset.ryState === 'readonly') {
      delete this.dataset.ryState;
    }
  }
}

customElements.define('ry-rating', RyRating);
